// NotificationBell — the web app's in-app notification inbox, rendered in the
// top header. A bell icon with an unread-count badge that opens a dropdown feed
// of everything the platform has notified this user about (task assignments,
// comments, status changes, mentions, email-capture prompts, ...).
//
// This is the durable record that pairs with the ephemeral push/email/Teams
// channels — the same feed the iOS app shows (app/notifications.tsx). Backed by
// GET /api/notifications (list + unreadCount) and the mark-read endpoints (see
// api/src/functions/notificationFeed.js).
//
// Clicking a row marks it read and navigates to the target via the `onNavigate`
// callback ({ type, id }) supplied by App.jsx. The unread count is polled every
// 60s so the badge stays current without a socket.
//
// The ⚙ opens the person's own settings — channels, phone number, quiet
// hours, connected accounts — which now live on a full page (pages/UserSettings
// .jsx, nav id "userSettings") rather than a modal here: `onOpenSettings` is
// how App.jsx wires the gear to `setPage('userSettings')`.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { B, radius, font, weight } from "../../constants/brand";
import { api } from "../../api/client";
import { Icon, EmptyState } from "../ui";
import { HeaderIconButton } from "./HeaderIconButton";
import { groupByNotificationWindow } from "@harvus/shared";
import { formatInstant } from '../../utils/format';

// Compact "3m / 2h / 4d / Jul 3" relative time.
function relativeTime(iso) {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Date.now() - then;
  const min = Math.floor(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d`;
  return formatInstant(iso, { month: "short", day: "numeric" });
}

const GLYPH = {
  task_assigned: "pushpin",
  task_status: "sync",
  task_comment: "message",
  task_mention: "mention",
  task_due_soon: "calendar",
  task_overdue: "warning",
  note_mention: "mention",
  deal_note: "note",
  deal_document: "file",
  deal_email: "emailInbox",
  deal_task: "pushpin",
  payment_scheduled: "ledger",
  payment_due: "ledger",
  payment_past_due: "warning",
};
const glyphFor = (kind) => GLYPH[kind] || "bell";

const POLL_MS = 60_000;

export function NotificationBell({ onNavigate, onOpenSettings }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("notifications");
      setItems(res.items || []);
      setUnread(res.unreadCount || 0);
    } catch {
      /* best-effort — leave the last good state */
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load + light polling for the badge. Also refetch when the panel is
  // opened so the list is fresh.
  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // The digest's email and push link land on ?view=notifications; App.jsx
  // turns that into this event so the feed opens where the reader arrived.
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener('harvus-open-notifications', onOpen);
    return () => window.removeEventListener('harvus-open-notifications', onOpen);
  }, []);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const markRead = useCallback(async (id) => {
    // Optimistic local flip, then reconcile.
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, isRead: true } : it)));
    setUnread((u) => Math.max(0, u - 1));
    try { await api.post(`notifications/${id}/read`, {}); } catch { /* ignore */ }
  }, []);

  const markAll = useCallback(async () => {
    setItems((prev) => prev.map((it) => ({ ...it, isRead: true })));
    setUnread(0);
    try { await api.post("notifications/read-all", {}); } catch { /* ignore */ }
  }, []);

  const onClickItem = useCallback((item) => {
    if (!item.isRead) markRead(item.id);
    if (item.link && onNavigate) onNavigate(item.link);
    setOpen(false);
  }, [markRead, onNavigate]);


  // Rows are one per event (the feed is the record); the heading is the
  // quarter-hour window the digest delivered them in, derived from the stamp.
  const windows = useMemo(() => groupByNotificationWindow(items), [items]);

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <HeaderIconButton icon="bell" label="Notifications" count={unread} active={open}
        onClick={() => setOpen((o) => !o)} />

      {open ? (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            right: 0,
            width: 380,
            maxWidth: "90vw",
            maxHeight: 480,
            display: "flex",
            flexDirection: "column",
            background: B.surface,
            border: `1px solid ${B.divider}`,
            borderRadius: radius.xxl,
            boxShadow: B.shadowMd,
            zIndex: 100,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 14px",
              borderBottom: `1px solid ${B.divider}`,
            }}
          >
            <span style={{ fontSize: font.md, fontWeight: weight.bold, color: B.text }}>Notifications</span>
            <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {unread > 0 ? (
                <button
                  onClick={markAll}
                  style={{ background: "none", border: "none", cursor: "pointer", fontSize: font.sm, fontWeight: weight.semibold, color: B.navy, padding: 0 }}
                >
                  Mark all read
                </button>
              ) : null}
              <button
                onClick={() => { setOpen(false); onOpenSettings?.(); }}
                aria-label="My Settings"
                title="My Settings"
                style={{ background: "none", border: "none", cursor: "pointer", color: B.textMd, padding: 0, display: "flex" }}
              >
                <Icon name="settings" size={15} />
              </button>
            </span>
          </div>

          <div style={{ overflowY: "auto" }}>
            {loading && items.length === 0 ? (
              <div style={{ padding: 24, textAlign: "center", color: B.textLt, fontSize: font.base }}>Loading…</div>
            ) : items.length === 0 ? (
              <EmptyState icon="bell" title="All caught up" message="No notifications yet." />
            ) : (
              windows.map((w) => (
              <div key={w.key}>
                <div style={{ padding: "8px 14px 4px", fontSize: 11, fontWeight: weight.semibold, color: B.textMd, letterSpacing: 0.4, textTransform: "uppercase", background: B.surfaceMuted, borderBottom: `1px solid ${B.divider}` }}>
                  {w.label}
                  <span style={{ fontWeight: weight.medium, color: B.textLt, marginLeft: 8, textTransform: "none", letterSpacing: 0 }}>
                    {w.items.length} {w.items.length === 1 ? "update" : "updates"}
                  </span>
                </div>
              {w.items.map((item) => (
                <button
                  key={item.id}
                  onClick={() => onClickItem(item)}
                  style={{
                    display: "flex",
                    gap: 10,
                    width: "100%",
                    textAlign: "left",
                    padding: "12px 14px",
                    border: "none",
                    borderBottom: `1px solid ${B.border}`,
                    background: item.isRead ? B.white : B.tealLt,
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  <span style={{ width: 22, display: "flex", justifyContent: "center", flexShrink: 0, marginTop: 1, color: item.isRead ? B.textMd : B.navy }}><Icon name={glyphFor(item.kind)} size={16} /></span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                      <span
                        style={{
                          flex: 1,
                          color: B.text,
                          fontSize: font.base,
                          fontWeight: item.isRead ? weight.medium : weight.bold,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.title}
                      </span>
                      <span style={{ color: B.textLt, fontSize: 11, flexShrink: 0 }}>{relativeTime(item.createdUtc)}</span>
                    </span>
                    {item.body ? (
                      <span
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          color: B.textMd,
                          fontSize: font.sm,
                          marginTop: 3,
                          lineHeight: 1.35,
                        }}
                      >
                        {item.body}
                      </span>
                    ) : null}
                  </span>
                  {item.isRead ? null : (
                    <span style={{ width: 8, height: 8, borderRadius: 4, background: B.navy, flexShrink: 0, marginTop: 5 }} />
                  )}
                </button>
              ))}
              </div>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
